import type { AfterViewInit } from '@angular/core';
import { Component, inject, Input } from '@angular/core';

import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DateTime } from 'luxon';

import type { ButtonColor } from '@cf/core';
import { injectEnvironment } from '@cf/core';
import { AuthService } from '@cf/temp/services';

import { ButtonComponent } from '@cf/shared/ui/button';
import { IconComponent } from '@cf/shared/ui/icons';

import { NgxTolgeeModule } from '@tolgee/ngx';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error
import {} from 'google.accounts';
import TokenClient = google.accounts.oauth2.TokenClient;
import TokenResponse = google.accounts.oauth2.TokenResponse;
import CodeClient = google.accounts.oauth2.CodeClient;
import CodeResponse = google.accounts.oauth2.CodeResponse;

@Component({
  selector: 'cf-google-auth',
  templateUrl: './google-auth.component.html',
  styleUrls: ['./google-auth.component.scss'],
  imports: [ButtonComponent, IconComponent, NgxTolgeeModule],
})
export class GoogleAuthComponent implements AfterViewInit {
  private readonly authService = inject(AuthService);
  private readonly modalService = inject(NgbModal);

  @Input() buttonName = '';
  @Input() isLogin = false;
  @Input() color: ButtonColor = 'primary';
  @Input() redirectToSignUp = false;

  private codeClient!: CodeClient;

  private tokenClient!: TokenClient;

  private environment = injectEnvironment();

  ngAfterViewInit() {
    setTimeout(() => {
      this.initGoogle();
    }, 500);
  }

  public authWithGoogle(): void {
    if (this.redirectToSignUp) {
      window.open(`${this.environment.appUrl}/auth/sign-up`, '_self');
      return;
    }
    if (this.isLogin) {
      this.tokenClient?.requestAccessToken();
    } else {
      this.codeClient?.requestCode();
    }
  }

  private initGoogle() {
    this.codeClient = google.accounts.oauth2.initCodeClient({
      client_id: this.environment.googleClientId,
      enable_serial_consent: false,
      // eslint-disable-next-line no-untranslated-string/no-untranslated-string
      scope: 'profile email https://www.googleapis.com/auth/calendar',
      redirect_uri: 'postmessage',
      callback: (codeResponse: CodeResponse) => {
        if (codeResponse.code) {
          this.authService
            .loginByGoogle({
              code: codeResponse.code,
              timezone: DateTime.local().zoneName,
            })
            .subscribe(() => {
              this.modalService.dismissAll();
            });
        }
      },
    });

    this.tokenClient = google?.accounts.oauth2.initTokenClient({
      client_id: this.environment.googleClientId,
      enable_serial_consent: false,
      // eslint-disable-next-line no-untranslated-string/no-untranslated-string
      scope: 'profile email',
      callback: (tokenResponse: TokenResponse) => {
        if (tokenResponse.access_token) {
          this.authService
            .loginByGoogleToken(
              {
                token: tokenResponse.access_token,
                timezone: DateTime.local().zoneName,
              },
              'google-token',
            )
            .subscribe(() => {
              this.modalService.dismissAll();
            });
        }
      },
    });
  }
}
